import { op, Operation } from "libs/transaction";
import { ActionCreator, createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import * as ops from "libs/operations";
import { generateRecordId, getPointer, isTagPrivate, SpecialTagTypeEnum, TagSubscriptionPreference } from "libs/schema";
import { difference } from "lodash-comms";
import dayjs from "dayjs";
import { getMessageContentForSubscriptionNotification } from "./utils/tag";
import { authorizeUserCanUpdateTag, authorizeUserHasAccessToTags } from "./authorizers/tagAuthorizers";
import { authorizeUserIsMemberOfOrganization } from "./authorizers/organizationAuthorizers";
import { PermissionError } from "../../errors";

/* -------------------------------------------------------------------------------------------------
 *  createGroup
 * -------------------------------------------------------------------------------------------------
 */

type CreateGroupProps = {
  ownerOrganizationId: string;
  groupId: string;
  icon: string | null;
  name: string;
  description: string | null;
  /** IDs of the tags which will act as folders for this tag */
  folderIds: string[];
  isPrivate: boolean;
};

export const createGroup = createAction<CreateGroupProps>("tag.createGroup", {
  operations: async (props, context) => {
    return [
      ...ops.group.createGroup({
        creatorUserId: context.currentUserId,
        isPrivateGroup: props.isPrivate,
        icon: props.icon,
        name: props.name,
        ownerOrganizationId: props.ownerOrganizationId,
        description: props.description,
        groupId: props.groupId,
        nestedInGroupIds: props.folderIds,
      }),
      ...ops.tag.setTagSubscription({
        tagId: props.groupId,
        tagType: SpecialTagTypeEnum.GROUP,
        creatorUserId: context.currentUserId,
        ownerOrganizationId: props.ownerOrganizationId,
        preference: "all-new",
        userId: context.currentUserId,
      }),
    ];
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToTags({
      tagIds: [props.ownerOrganizationId, ...props.folderIds],
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  updateGroup
 * -------------------------------------------------------------------------------------------------
 */

type UpdateGroupProps = {
  ownerOrganizationId: string;
  tagId: string;
  icon: string | null;
  name: string;
  description: string | null;
  /** IDs of the tags which will act as folders for this tag */
  folderIds: string[];
};

export const updateGroup = createAction<UpdateGroupProps>("tag.updateGroup", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const operations: Operation[] = [];

    const [[oldTag], [oldFolders]] = await Promise.all([
      loader.getRecord("tag", props.tagId),
      loader.getTagFolderMembers({
        tag_id: props.tagId,
      }),
    ]);

    if (!oldTag) {
      throw new InvalidActionStateError("[updateGroup] Old tag not found", { errorData: { id: props.tagId } });
    }

    operations.push(
      op.update(getPointer("tag", props.tagId), {
        name: props.name,
        icon: props.icon,
        description: props.description,
      }),
    );

    const oldFolderIds = oldFolders.map((folder) => folder.folder_id);
    const newFolderIds = difference(props.folderIds, oldFolderIds);
    const removedFolderIds = difference(oldFolderIds, props.folderIds);

    removedFolderIds.forEach((folderId) => {
      operations.push(
        op.delete(
          getPointer("tag_folder_member", {
            tag_id: props.tagId,
            folder_id: folderId,
          }),
        ),
      );
    });

    newFolderIds.forEach((folderId) => {
      const pointer = getPointer("tag_folder_member", {
        tag_id: props.tagId,
        folder_id: folderId,
      });

      operations.push(
        op.set("tag_folder_member", {
          id: pointer.id,
          tag_id: props.tagId,
          tag_type: SpecialTagTypeEnum.GROUP,
          folder_id: folderId,
          creator_user_id: context.currentUserId,
          owner_organization_id: props.ownerOrganizationId,
        }),
      );
    });

    return operations;
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToTags({
      tagIds: [props.tagId, ...props.folderIds],
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  createLabel
 * -------------------------------------------------------------------------------------------------
 */

type CreateLabelProps = {
  ownerOrganizationId: string;
  labelId: string;
  icon: string | null;
  name: string;
  description: string | null;
};

export const createLabel = createAction<CreateLabelProps>("tag.createLabel", {
  operations: async (props, context) => {
    return ops.label.createLabel({
      icon: props.icon,
      name: props.name,
      ownerOrganizationId: props.ownerOrganizationId,
      description: props.description,
      labelId: props.labelId,
      isPrivateLabel: true,
      creatorUserId: context.currentUserId,
    });
  },
  authorize: async (props, context) => {
    await authorizeUserIsMemberOfOrganization({ organizationId: props.ownerOrganizationId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  updateLabel
 * -------------------------------------------------------------------------------------------------
 */

type UpdateLabelProps = {
  labelId: string;
  icon: string | null;
  name: string;
  description: string | null;
};

export const updateLabel = createAction<UpdateLabelProps>("tag.updateLabel", {
  operations: async (props) => {
    return op.update(getPointer("tag", props.labelId), {
      name: props.name,
      icon: props.icon,
      description: props.description,
    });
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateTag({
      tagId: props.labelId,
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  deleteLabel
 * -------------------------------------------------------------------------------------------------
 */

type DeleteLabelProps = {
  labelId: string;
};

export const deleteLabel = createAction<DeleteLabelProps>("tag.deleteLabel", {
  operations: async (props, context) => {
    return [
      op.delete("tag", props.labelId),
      op.delete(getPointer("tag_user_member", { user_id: context.currentUserId, tag_id: props.labelId })),
    ];
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateTag({
      tagId: props.labelId,
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  subscribeUsersToTag
 * -------------------------------------------------------------------------------------------------
 */

type SubscribeUsersToTagProps = {
  ownerOrganizationId: string;
  tagId: string;
  userIds: string[];
  subscriptionPreference: TagSubscriptionPreference;
  notifyUsers: boolean;
  /**
   * This is generated by the client and sent to the server
   */
  notificationMessageIdByUserId?: { [userId: string]: string };
};

export const subscribeUsersToTag: ActionCreator<SubscribeUsersToTagProps> = {
  name: "tag.subscribeUsersToTag",
  action: async (props, context) => {
    const { loader } = context.environment;

    const operations: Operation[] = [];

    const userProfilePointers = props.userIds.map((userId) => getPointer("user_profile", userId));

    const [[tag], [userProfiles]] = await Promise.all([
      loader.getRecord("tag", props.tagId),
      loader.getRecords(userProfilePointers),
    ]);

    if (!tag) {
      throw new InvalidActionStateError("[subscribeUsersToTag] Tag not found", { errorData: { id: props.tagId } });
    }

    const isPrivate = isTagPrivate(tag);

    props.userIds.forEach((userId) => {
      const pointer = getPointer("tag_subscription", {
        tag_id: props.tagId,
        user_id: userId,
      });

      operations.push(
        op.upsert(pointer, {
          onCreate: [
            op.set(pointer.table, {
              id: pointer.id,
              tag_id: props.tagId,
              tag_type: tag.type,
              user_id: userId,
              creator_user_id: context.currentUserId,
              preference: props.subscriptionPreference,
              owner_organization_id: props.ownerOrganizationId,
            }),
          ],
          onUpdate: [
            {
              type: "upsert_on_update",
              where: {
                preference: { eq: "involved" },
              },
              operations: [
                op.update(pointer, {
                  preference: props.subscriptionPreference,
                }),
              ],
            },
          ],
        }),
      );

      if (isPrivate) {
        const pointer = getPointer("tag_user_member", {
          tag_id: props.tagId,
          user_id: userId,
        });

        operations.push(
          op.upsert(pointer, {
            onCreate: [
              op.set(pointer.table, {
                id: pointer.id,
                tag_id: props.tagId,
                tag_type: tag.type,
                user_id: userId,
                creator_user_id: context.currentUserId,
                owner_organization_id: props.ownerOrganizationId,
              }),
            ],
          }),
        );
      }
    });

    const newNotificationMessageIdByUserId: { [userId: string]: string } = {};

    if (props.notifyUsers) {
      const now = dayjs().add(30, "seconds").toDate();

      props.userIds.forEach((userId) => {
        const userProfile = userProfiles.find((profile) => profile.id === userId)?.record;

        if (!userProfile) {
          throw new InvalidActionStateError("[subscribeUsersToTag] User profile not found", { errorData: { userId } });
        }

        const subjectText =
          isPrivate ?
            `You've been subscribed to the private "#${tag.name}" group`
          : `You've been subscribed to the "#${tag.name}" group`;

        const bodyHtml = getMessageContentForSubscriptionNotification({
          user: userProfile,
          tag,
        });

        let draftId: string;

        if (context.type === "client") {
          draftId = generateRecordId("draft");
          newNotificationMessageIdByUserId[userId] = draftId;
        } else {
          // The fallback case is not ideal, but at least we'll ensure delivery
          draftId = props.notificationMessageIdByUserId?.[userId] || generateRecordId("draft");
        }

        ops.applyOperationsToTransaction(
          operations,
          ops.draft.sendNewThreadDraft({
            scheduledToBeSentAt: now,
            scheduledToBeSentAtTz: null,
            draft: {
              id: draftId,
              type: "COMMS",
              to: [
                {
                  id: generateRecordId("draft_user_recipient_doc", {
                    type: "USER",
                    user_id: userId,
                  }),
                  type: "USER",
                  user_id: userId,
                  is_implicit: false,
                  is_mentioned: true,
                  priority: 300,
                },
              ],
              body_html: bodyHtml,
              attachments: [],
              labels: [],
              branched_from_message_id: null,
              branched_from_thread_id: null,
              is_reply: false,
              is_edit: false,
              new_thread_subject: subjectText,
              new_thread_visibility: "PRIVATE",
              owner_organization_id: props.ownerOrganizationId,
              thread_id: generateRecordId("thread"),
              user_id: context.currentUserId,
            },
            branchedFromMessage: undefined,
          }),
        );
      });
    }

    return {
      name: "tag.subscribeUsersToTag",
      props: {
        ...props,
        notificationMessageIdByUserId: newNotificationMessageIdByUserId,
      },
      operations,
    };
  },
  authorize: async (props, context) => {
    const [tag] = await context.environment.loader.getRecord("tag", props.tagId);

    if (!tag) {
      throw new PermissionError("User doesn't have access to tag");
    }

    await Promise.all(
      props.userIds.map((userId) =>
        authorizeUserIsMemberOfOrganization({
          organizationId: tag.owner_organization_id,
          context: {
            ...context,
            currentUserId: userId,
          },
        }),
      ),
    );
  },
};

/* -------------------------------------------------------------------------------------------------
 *  archiveTag
 * -------------------------------------------------------------------------------------------------
 */

type ArchiveTagProps = {
  tagId: string;
};

export const archiveTag = createAction<ArchiveTagProps>("tag.archiveTag", {
  operations: async (props): Promise<Operation | Operation[]> => {
    return op.update(getPointer("tag", props.tagId), { archived_at: op.fieldValue.SERVER_TIMESTAMP() });
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateTag({ tagId: props.tagId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  unarchiveTag
 * -------------------------------------------------------------------------------------------------
 */

type UnarchiveTagProps = {
  tagId: string;
};

export const unarchiveTag = createAction<UnarchiveTagProps>("tag.unarchiveTag", {
  operations: async (props): Promise<Operation | Operation[]> => {
    return op.update(getPointer("tag", props.tagId), { archived_at: null });
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateTag({ tagId: props.tagId, context });
  },
});

export const actions = {
  createGroup,
  updateGroup,
  createLabel,
  updateLabel,
  deleteLabel,
  subscribeUsersToTag,
  archiveTag,
  unarchiveTag,
};
